/*
 * Where a conversation opens (docs/2026-09-21-inbox-view-design.md, change 5). The rep opens a row
 * because the lead wrote, so the thread opens on the lead's newest message and whatever followed it;
 * everything earlier sits behind one "Show N earlier messages" control. Until 2026-09-21 the pane
 * began with the seller's first cold email, so the reason for opening was a scroll away.
 *
 * With no inbound message at all, the newest message is shown on its own. Pure, so the browser and
 * the tests share it.
 */
import type { EmailDisplay } from "./email-display.ts";

/** A quote read and not matched to this thread: mail outside it, or one the parser gave up on. */
const UNPROVED_QUOTE: ReadonlySet<string> = new Set(["unconfirmed", "uncertain"]);

export function threadOpening<M extends { direction: "inbound" | "outbound" }>(
  messages: readonly M[],
): { earlier: M[]; shown: M[] } {
  const newestInbound = messages.findLastIndex((message) => message.direction === "inbound");
  const start = newestInbound >= 0 ? newestInbound : Math.max(0, messages.length - 1);
  return { earlier: messages.slice(0, start), shown: messages.slice(start) };
}

/**
 * The seller's message the lead's newest one answers, which the folded thread previews above it: the
 * newest of ours its quote was proved to copy, else the newest of ours among the earlier ones. A lead
 * often points at it ("those slots", "your price") and it would otherwise sit behind the pill. Null
 * when the thread does not open on a lead message or nothing of ours came before it, and when the
 * lead's quote holds mail this thread does not have: the lead answered something we cannot show, so
 * naming our last message here would send the rep to the wrong email.
 */
export function answeredMessage<M extends { id: string; direction: "inbound" | "outbound" }>(
  messages: readonly M[],
  lead: Pick<EmailDisplay, "fold" | "regions">,
): M | null {
  const { earlier, shown } = threadOpening(messages);
  if (shown[0]?.direction !== "inbound") return null;
  if (lead.fold.kind === "shown" && UNPROVED_QUOTE.has(lead.fold.reason)) return null;
  const ours = earlier.filter((message) => message.direction === "outbound");
  const quoted = lead.regions.flatMap((region) =>
    region.kind === "duplicate_quote" ? region.representedBy : [],
  );
  return ours.findLast((message) => quoted.includes(message.id)) ?? ours.at(-1) ?? null;
}

/** Longer than three lines at any pane width; CSS clamps what is drawn. */
const PREVIEW_CHARACTERS = 400;

/** About what three lines show: a question past here would be cut by the clamp. */
const FIRST_LINES = 200;

/*
 * A lead points at what we asked ("those slots"), and in real seller mail that sits low: of 2,799
 * emails holding a question, 2,440 had the last one past the first 200 characters, and one of those
 * read like an opt-out line (2026-11-02, a count over `.local/corpus/pairs`, the seller's last message
 * before the lead's first reply). Only the punctuation is read, so no list of greetings to keep.
 */
const fromLastQuestion = (flat: string): string => {
  const question = flat.lastIndexOf("?");
  if (question < FIRST_LINES) return flat;
  const before = flat.slice(0, question);
  const start = Math.max(...[". ", "! ", "? "].map((end) => before.lastIndexOf(end))) + 2;
  return start > 1 ? `…${flat.slice(start)}` : flat;
};

/**
 * A message's words as one line of preview text: whitespace collapsed, from the sentence of its last
 * question when the first lines would not reach it, cut past what is drawn.
 */
export function previewWords(text: string): string {
  const words = fromLastQuestion(text.replace(/\s+/gu, " ").trim());
  return words.length > PREVIEW_CHARACTERS ? `${words.slice(0, PREVIEW_CHARACTERS - 1)}…` : words;
}
